import { StaticImageData } from "next/image";
import clickUp from "@/assets/images/icons/integrations/clickup.svg";
import asana from "@/assets/images/icons/integrations/asana.svg";
import airtable from "@/assets/images/icons/integrations/airtable.svg";
import smartsheet from "@/assets/images/icons/integrations/smartsheet.svg";
import tickTick from "@/assets/images/icons/integrations/ticktick.svg";
import basecamp from "@/assets/images/icons/integrations/basecamp.svg";
import todoist from "@/assets/images/icons/integrations/todoist.svg";
import linear from "@/assets/images/icons/integrations/linear.svg";
import trello from "@/assets/images/icons/integrations/trello.svg";
import googleDrive from "@/assets/images/icons/integrations/google-drive.svg";
import confluence from "@/assets/images/icons/integrations/confluence.svg";
import jira from "@/assets/images/icons/integrations/jira.svg";
import github from "@/assets/images/icons/integrations/github.svg";
import gitlab from "@/assets/images/icons/integrations/gitlab.svg";
import bitbucket from "@/assets/images/icons/integrations/bitbucket.svg";
import gmail from "@/assets/images/icons/integrations/gmail.svg";
import microsoftOutlook from "@/assets/images/icons/integrations/microsoft-outlook.svg";
import googleChats from "@/assets/images/icons/integrations/google-chats.svg";
import googleCalendar from "@/assets/images/icons/integrations/google-calendar.svg";
import outlookCalendar from "@/assets/images/icons/integrations/outlook-calendar.svg";
import slack from "@/assets/images/icons/integrations/slack.svg";
import microsoftTeams from "@/assets/images/icons/integrations/microsoft-teams.svg";
import hubspot from "@/assets/images/icons/integrations/hubspot.svg";
import salesforce from "@/assets/images/icons/integrations/salesforce.svg";
import whatsappBusiness from "@/assets/images/icons/integrations/whatsapp-business.svg";
import jiraServiceManagement from "@/assets/images/icons/integrations/jira-sm.svg";
import sharePoint from "@/assets/images/icons/integrations/sharepoint.svg";
import intercom from "@/assets/images/icons/integrations/intercom.svg";
import freshdesk from "@/assets/images/icons/integrations/freshdesk.svg";
import oneDrive from "@/assets/images/icons/integrations/onedrive.svg";
import clockify from "@/assets/images/icons/integrations/clockify.svg";
import wrike from "@/assets/images/icons/integrations/wrike.svg";
import notion from "@/assets/images/icons/integrations/notion.svg";
import googleDoc from "@/assets/images/icons/integrations/google-doc.svg";

export type Integration = {
  id: number;
  icon: StaticImageData | string;
  title: string;
  description: string;
  alt?: string;
  url?: string;
};

export type IntegrationsData = {
  [key: string]: { label: string; listData: Integration[] };
};

export const integrationsData: IntegrationsData = {
  projectManagement: {
    label: "Project Management",
    listData: [
      {
        id: 1,
        icon: clickUp,
        title: "ClickUp",
        url: "/integrations/clickup",
        description:
          "Centralize tasks, projects, and workflows for seamless management and collaboration.",
      },
      {
        id: 2,
        icon: asana,
        title: "Asana",
        url: "/integrations/asana",
        description:
          "Manage tasks and workflows more effectively by integrating Asana with Kroolo.",
      },
      {
        id: 3,
        icon: airtable,
        title: "Airtable",
        url: "/integrations/airtable",
        description:
          "Manage projects, assign tasks, and track progress to improve team collaboration & efficiency.",
      },
      {
        id: 4,
        icon: smartsheet,
        title: "Smartsheet",
        url: "/integrations/smartsheet",
        description:
          "Manage projects, automate processes, and collaborate with real-time updates.",
      },
      {
        id: 5,
        icon: wrike,
        title: "Wrike",
        url: "/integrations/wrike",
        description:
          "Manage projects, tasks, and workflows with Wrike’s powerful tools.",
      },
    ],
  },
  taskManagement: {
    label: "Task Management",
    listData: [
      {
        id: 1,
        icon: tickTick,
        title: "TickTick",
        url: "/integrations/ticktick",
        description:
          "Manage tasks, set reminders & stay on track with TickTick’s powerful productivity tools.",
      },
      {
        id: 2,
        icon: basecamp,
        title: "Basecamp",
        url: "/integrations/basecamp",
        description:
          "Organize projects, to-dos, & communication in one place with Basecamp’s intuitive platform.",
      },
      {
        id: 3,
        icon: todoist,
        title: "Todoist",
        url: "/integrations/todoist",
        description:
          "Stay organized and focused by managing tasks, projects, and priorities with Todoist.",
      },
      {
        id: 4,
        icon: linear,
        title: "Linear",
        url: "/integrations/linear",
        description:
          "Track and manage software projects with Linear’s streamlined issue tracking & agile workflows.",
      },
      {
        id: 5,
        icon: trello,
        title: "Trello",
        url: "/integrations/trello",
        description:
          "Organize tasks, collaborate with your team, and visually track projects using Trello boards.",
      },
    ],
  },
  calendarAndCommunication: {
    label: "Calendar & Communication",
    listData: [
      {
        id: 1,
        icon: gmail,
        title: "Gmail",
        url: "/integrations/gmail",
        description:
          "Integrate Gmail with Kroolo to manage communication within your projects seamlessly.",
      },
      {
        id: 2,
        icon: microsoftOutlook,
        title: "Outlook",
        url: "/integrations/microsoft-outlook",
        description:
          "Sync your Microsoft Outlook emails and calendar with Kroolo for efficient project management.",
      },
      {
        id: 3,
        icon: googleCalendar,
        title: "Google Calendar",
        url: "/integrations/google-calendar",
        description:
          "Sync your Google Calendar with Kroolo to track project deadlines and meetings.",
      },
      {
        id: 4,
        icon: outlookCalendar,
        title: "Outlook Calendar",
        url: "/integrations/outlook-calendar",
        description:
          "Integrate Outlook Calendar with Kroolo for improved scheduling and task management.",
      },
    ],
  },
  productAndEngineering: {
    label: "Product & Engineering",
    listData: [
      {
        id: 1,
        icon: jira,
        title: "Jira",
        url: "/integrations/jira",
        description:
          "Manage, track, and release software with Jira’s agile project management tools.",
      },
      {
        id: 2,
        icon: github,
        title: "GitHub",
        url: "/integrations/github",
        description:
          "Integrate GitHub to manage code, pull requests, and commits directly within your workflow.",
      },
      {
        id: 3,
        icon: gitlab,
        title: "GitLab",
        url: "/integrations/gitlab",
        description:
          "Link GitLab commits, branches, and pull requests directly to tasks for streamlined development.",
      },
      {
        id: 4,
        icon: bitbucket,
        title: "Bitbucket",
        url: "/integrations/bitbucket",
        description:
          "Collaborate on code, manage repositories, and deploy directly within Bitbucket for streamlined development.",
      },
    ],
  },
  docsAndFiles: {
    label: "Docs & Files",
    listData: [
      {
        id: 1,
        icon: googleDoc,
        title: "Google Docs",
        url: "/integrations/google-docs",
        description:
          "Create, share, and collaborate on documents seamlessly with Google Docs integration.",
      },
      {
        id: 2,
        icon: googleDrive,
        title: "Google Drive",
        url: "/integrations/google-drive",
        description:
          "Store, share, and collaborate on documents seamlessly with Google Drive integration.",
      },
      {
        id: 3,
        icon: confluence,
        title: "Confluence",
        url: "/integrations/confluence",
        description:
          "Create, share, and collaborate on knowledge and documentation with Confluence’s powerful tools.",
      },
      {
        id: 4,
        icon: oneDrive,
        title: "OneDrive",
        url: "/integrations/one-drive",
        description:
          "Store, share, and collaborate on files securely with OneDrive integration in Kroolo.",
      },
      {
        id: 5,
        icon: sharePoint,
        title: "SharePoint",
        url: "/integrations/sharepoint",
        description:
          "Integrate SharePoint to manage documents, collaborate on projects, and streamline workflows.",
      },
      {
        id: 6,
        icon: notion,
        title: "Notion",
        url: "/integrations/notion",
        description:
          "Manage projects, tasks, and workflows with Notion’s powerful tools.",
      },
    ],
  },
  serviceManagement: {
    label: "Service & Time Management",
    listData: [
      {
        id: 1,
        icon: jiraServiceManagement,
        title: "Jira Service Management",
        url: "/integrations/jira-service-management",
        description:
          "Manage service requests, incidents, and changes with Jira Service Management integration.",
      },
      {
        id: 2,
        icon: intercom,
        title: "Intercom",
        url: "/integrations/intercom",
        description:
          "Enhance customer support and engagement with Intercom’s messaging and automation tools.",
      },
      {
        id: 3,
        icon: freshdesk,
        title: "Fresh Service",
        url: "/integrations/freshservice",
        description:
          "Manage customer support tickets, automate workflows, and enhance customer service with FreshService.",
      },
      {
        id: 4,
        icon: clockify,
        title: "Clockify",
        url: "/integrations/clockify",
        description:
          "Manage projects, tasks, and workflows with Clockify’s powerful tools.",
      },
    ],
  },
  collaborationAndChat: {
    label: "Collaboration & Chat",
    listData: [
      {
        id: 1,
        icon: slack,
        title: "Slack",
        url: "/integrations/slack",
        description:
          "Integrate Slack to streamline communication, manage tasks, and receive updates in real-time.",
      },
      {
        id: 2,
        icon: microsoftTeams,
        title: "Microsoft Teams",
        url: "/integrations/microsoft-teams",
        description:
          "Collaborate, share files, and manage tasks all within Microsoft Teams seamlessly.",
      },
      {
        id: 3,
        icon: whatsappBusiness,
        title: "WhatsApp Business",
        url: "/integrations/whatsapp-business",
        description:
          "Connect with customers and team members through WhatsApp Business for efficient communication.",
      },
      // {
      //   id: 4,
      //   icon: googleChats,
      //   title: "Google Chats",
      //   url: "/integrations/google-chats",
      //   description:
      //     "Enhance team communication through Google Chats integration with Kroolo.",
      // },
    ],
  },
  crm: {
    label: "CRM",
    listData: [
      {
        id: 1,
        icon: hubspot,
        title: "Hubspot",
        url: "/integrations/hubspot",
        description:
          "Automate and sync HubSpot’s CRM tools to optimize sales, marketing, and customer relations.",
      },
      {
        id: 2,
        icon: salesforce,
        title: "Salesforce",
        url: "/integrations/salesforce",
        description:
          "Integrate Salesforce to streamline customer management, sales, and team collaboration processes.",
      },
    ],
  },
};

export const allIntegrationData: Integration[] = Object.values(integrationsData)
  .map((integration) => integration.listData)
  .flat();
